import React from 'react';
import moment from 'moment';
import Button from 'components/UI/Button';
import QRCodeIcon from 'assets/img/pic/qrcode-icon.png';
import ArrowIcon from 'assets/img/svg/arrow.svg';
import { useNavigate } from 'react-router-dom';
import { ISession } from 'types/session';
import Avatar from 'assets/img/svg/avatar.svg';
import classes from '../AdminLayout/Header/styles.module.scss';
import styles from './styles.module.scss';
import Topbar from '../AdminLayout/Topbar';
import ProfileMenu from '../AdminLayout/Topbar/ProfileMenu';
import cn from 'classnames';
import { useUnit } from 'effector-react';
import { $user } from 'models/user';
import { formatUserFullName } from 'utils/helpers';
import useFetch from 'use-http';

type TsdLoginData = {
	currentPinCode?: string | null;
	currentPinCodeExpiryDate?: string | null;
	nextPinCode?: string | null;
};

const isTsdLoginButtonEnabled = process.env.REACT_APP_TSD_LOGIN_BUTTON_ENABLED === 'true';

const formatPinDate = (date?: string | null) => {
	if (!date) {
		return '-';
	}

	const parsedDate = moment(date);
	return parsedDate.isValid() ? parsedDate.format('DD.MM.YYYY HH:mm:ss') : date;
};

export const Card = ({ session, variant = '' }: { session?: ISession; variant: string }) => {
	const navigate = useNavigate();

	return (
		<div className="column-xs-12">
			<div className={[styles.card, styles[`card--${variant}`]].join(' ')}>
				<div className={styles['card-left-side']}>
					{session ? (
						<h2>
							{session?.begin?.timezoneOffset
								? moment(session.begin?.createdAt)
										.utcOffset(-1 * session.begin?.timezoneOffset)
										.format('HH:mm')
								: moment(session.begin?.createdAt).format('HH:mm')}
							{' - '}
							{session.end ? (
								<>
									{session.end.timezoneOffset
										? moment(session.end.createdAt)
												.utcOffset(-1 * session.end.timezoneOffset)
												.format('HH:mm')
										: moment(session.end.createdAt).format('HH:mm')}
								</>
							) : (
								'...'
							)}
						</h2>
					) : (
						<h2>Нет активных сессий</h2>
					)}

					{session ? (
						<p>
							{session?.store?.number} {session?.store?.title}
						</p>
					) : (
						<p>Нажмите на значок “QR” ниже, чтобы открыть сессию</p>
					)}
				</div>
				<div className={styles['card-right-side']}>
					{variant === 'open' && (
						<>
							<span className={styles['card-mode--text']}>Активна</span>
							<Button size="sm" variant="orange" onClick={() => navigate('/scanner/close')}>
								Закрыть
							</Button>
						</>
					)}
					{variant === 'close' && <span className={styles['card-mode--text card-mode--text-close']}>Закрыта</span>}
				</div>
			</div>
		</div>
	);
};

export default function Session({ data, disable }: { data: ISession[]; disable: boolean }) {
	const navigate = useNavigate();
	const user = useUnit($user);
	const { get } = useFetch();
	const [tsdLoginData, setTsdLoginData] = React.useState<TsdLoginData | null>(null);
	const [isTsdLoginPopupOpen, setIsTsdLoginPopupOpen] = React.useState(false);
	const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	const closeTsdLoginPopup = React.useCallback(() => {
		setIsTsdLoginPopupOpen(false);
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	}, []);

	const resetTsdLoginPopupTimer = React.useCallback(() => {
		if (closeTimerRef.current) {
			clearTimeout(closeTimerRef.current);
		}

		closeTimerRef.current = setTimeout(closeTsdLoginPopup, 5 * 60 * 1000);
	}, [closeTsdLoginPopup]);

	const openTsdLoginPopup = React.useCallback(async () => {
		let loginData = tsdLoginData;

		if (!loginData) {
			loginData = await get('/api/users/me');
			setTsdLoginData(loginData);
		}

		setIsTsdLoginPopupOpen(true);
		resetTsdLoginPopupTimer();
	}, [get, resetTsdLoginPopupTimer, tsdLoginData]);

	React.useEffect(() => {
		if (!isTsdLoginPopupOpen) {
			return;
		}

		const events = ['click', 'keydown', 'touchstart', 'scroll'];
		events.forEach(eventName => window.addEventListener(eventName, resetTsdLoginPopupTimer, { passive: true }));

		return () => {
			events.forEach(eventName => window.removeEventListener(eventName, resetTsdLoginPopupTimer));
		};
	}, [isTsdLoginPopupOpen, resetTsdLoginPopupTimer]);

	React.useEffect(() => {
		return () => {
			if (closeTimerRef.current) {
				clearTimeout(closeTimerRef.current);
			}
		};
	}, []);

	return (
		<div className={styles['wrap']}>
			<div className={styles['top-side']}>
				<h2 className={styles['top-side-left']}>Сессии сегодня</h2>
				<div className={styles['counter']}>
					<div className={styles['counter-value']}>{data.length}</div>
					<div className={styles['counter-label']}>Открытых</div>
				</div>
				<div className={styles['top-side-right']}>
					<div className={classes.header__bar}>
						<div className={classes['header__bar-item']}>
							<div className={`${classes['profile-bar']} profile-bar`}>
								<div className={classes['profile-bar__wrap']}>
									<div className={classes['profile-bar__main']}>
										<div className={classes['profile-bar__name']}>{formatUserFullName(user)}</div>
										<div className={classes['profile-bar__sub']}></div>
									</div>
									<div className={classes['profile-bar__avatar']}>
										<div className={cn(classes.avatar, classes['avatar--md'])}>
											<img width="50px" src={Avatar} alt="" />
										</div>
									</div>
								</div>
								<Topbar>
									<ProfileMenu />
								</Topbar>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={styles['body-side']}>
				<div className="row">
					{data?.length > 0 ? (
						data.map((session: ISession) => {
							return <Card key={session.id} session={session} variant={session.endId ? 'close' : 'open'} />;
						})
					) : (
						<Card variant="empty" />
					)}
				</div>
			</div>
			<div className={styles['bottom-side']}>
				<div className={styles['navigate']}>
					{isTsdLoginPopupOpen && (
						<div className={styles['tsd-popup']}>
							<div className={styles['tsd-popup-content']}>
								<p>Текущий пин-код: {tsdLoginData?.currentPinCode || '-'}</p>
								<p>
									Дата действия текущего пин-кода:{' '}
									{formatPinDate(tsdLoginData?.currentPinCodeExpiryDate)}
								</p>
								<p>Новый пин-код: {tsdLoginData?.nextPinCode || '-'}</p>
							</div>
							<Button size="sm" variant="orange" onClick={closeTsdLoginPopup}>
								Ок
							</Button>
						</div>
					)}
					<div className={styles['navigate-page']}>
						<button type="button" className={styles['navigate-button']} onClick={() => navigate('/history')}>
							История
						</button>{' '}
						<img width="10" style={{ display: 'initial' }} src={ArrowIcon} />
					</div>
					{isTsdLoginButtonEnabled && (
						<div className={styles['navigate-tsd']}>
							<button type="button" className={styles['navigate-button']} onClick={openTsdLoginPopup}>
								Данные для входа на ТСД
							</button>
						</div>
					)}
					<div className={styles['navigate-qrcode']}>
						<button
							className={`${styles['navigate-qrcode']} ${disable ? styles['disabled'] : ''}`}
							onClick={!disable ? () => navigate('/scanner/open') : undefined}
							type="button"
						>
							<img src={QRCodeIcon} alt="Сканировать QR" />
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
